import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, MenuItem, Select, TextField, Typography } from '@mui/material';
import { Add, Remove, Check, Close } from '@mui/icons-material';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteIcon from '@mui/icons-material/Delete';
import { useViewerStore, ViewerTabsMode } from 'context/ViewerContext';

type AdditionSpinnerProps = {
  height: number;
  hardness: any;
  onHeightChange: (v: number) => void;
  onHardnessChange: (v: any) => void;
  onDelete?: () => void;
  onOpen?: () => void;
  onClose?: () => void;
  min?: number;
  max?: number;
  step?: number;
  name?: string;
  color?: string;
};

const AdditionSpinner: React.FC<AdditionSpinnerProps> = ({
  height,
  hardness,
  onHeightChange,
  onHardnessChange,
  onDelete,
  onOpen,
  onClose,
  min = 0,
  max = 10,
  step = 1,
  name = 'Addition',
  color = 'var(--toolbar-button-border)',
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const [tempHeight, setTempHeight] = useState<number>(height);
  const [tempHardness, setTempHardness] = useState<any>(hardness);
  const [isHardnessMenuOpen, setIsHardnessMenuOpen] = useState(false);
  const holdTimeout = useRef<NodeJS.Timeout | null>(null);
  const holdInterval = useRef<NodeJS.Timeout | null>(null);
  let rightToolboxMode = useViewerStore((state) => state.mode);

  const containerRef = useRef<HTMLDivElement>(null);

  const themeColor = color;

  const hardnessOptions = useMemo(() => {
    return ['Extra Hard', 'Hard', 'Medium', 'Soft', 'Extra Soft'];
  }, []);

  const clampFloor = (v: number) => {
    // const floored = Math.floor(v);
    const floored = step >= 1 ? Math.round(v) : Math.round(v * 100) / 100; // Round to 2 decimal places for step < 1);
    return Math.min(max, Math.max(min, floored));
  };

  const handleHeightInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = parseFloat(event.target.value); 
    if (Number.isNaN(newValue)) newValue = min;
    setTempHeight(clampFloor(newValue));
  };

  const handleApply = () => {
    const heightChanged = tempHeight !== height;
    const hardnessChanged = tempHardness !== hardness;

    if (heightChanged && hardnessChanged) {
      onHeightChange(tempHeight);

      setTimeout(() => {
        onHardnessChange(tempHardness);
      }, 1000);
    } else {
      if (heightChanged) {
        onHeightChange(tempHeight);
      }

      if (hardnessChanged) {
        onHardnessChange(tempHardness);
      }
    }

    setIsEditing(false);
    onClose?.();
  };

  const handleCancel = () => {
    setTempHeight(height);
    setTempHardness(hardness);
    setIsEditing(false);
    onClose?.();
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete();
    }
    onClose?.();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (event.button !== 2) return;

      if (isHardnessMenuOpen) return;

      if (
        isEditing &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        handleCancel();
      }
    };

    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, [isEditing, height, hardness, isHardnessMenuOpen]);

  const startHold = (direction: 'inc' | 'dec') => {
    stopHold();

    const action = () => {
      setTempHeight((prev) =>
        clampFloor(prev + (direction === 'inc' ? step : -step))
      );
    };

    action();

    holdTimeout.current = setTimeout(() => {
      holdInterval.current = setInterval(action, 60);
    }, 300);
  };

  const stopHold = () => {
    if (holdTimeout.current) {
      clearTimeout(holdTimeout.current);
      holdTimeout.current = null;
    }

    if (holdInterval.current) {
      clearInterval(holdInterval.current);
      holdInterval.current = null;
    }
  };

  useEffect(() => {
    return () => {
      stopHold();
    };
  }, []);

  useEffect(() => {
    if (!isEditing) return;
    handleCancel();
  }, [rightToolboxMode]);


  if (!isEditing) {
    return (
      <Box
        // The assistant opens this label by clicking it, the same way the
        // parameter spinners are opened, so it goes through the same path a
        // user's click takes and the other labels hide themselves.
        data-voice-open="1"
        onClick={() => {
          setTempHeight(clampFloor(height));
          setTempHardness(hardness);
          setIsEditing(true);
          onOpen?.();
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `1px solid ${themeColor}`,
          borderRadius: '50%',
          bgcolor: 'var(--spinner-bg)',
          backdropFilter: 'blur(8px)',
          width: 24,
          height: 24,
          cursor: 'pointer',
          transition: '0.15s ease',
          '&:hover': {
            bgcolor: 'var(--spinner-hover-bg)',
          },
        }}
      >
        <SettingsIcon
          sx={{
            fontSize: 14,
            color: themeColor,
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Box
        sx={{
          bgcolor: 'var(--spinner-bg)',
          backdropFilter: 'blur(8px)',
          borderRadius: '12px',
          border: `1px solid ${themeColor}`,
          padding: '6px 10px',
          minWidth: '160px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          transition: 'all 0.2s ease',
          textAlign: 'center',
          whiteSpace: 'nowrap',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 0.5,
          }}
        >
          <Typography
            sx={{
              fontSize: '10px',
              fontWeight: 500,
              color: themeColor,
              letterSpacing: '0.3px',
              fontFamily: 'monospace',
              textAlign: 'center',
              width: '100%',
            }}
          >
            {name}
            <IconButton
              onClick={handleDelete}
              sx={{
                width: 20,
                height: 20,
                color: '#d32f2f',
                ml: 1,
                '&:hover': {
                  bgcolor: 'rgba(211, 47, 47, 0.1)',
                  color: '#d32f2f',
                },
              }}
            >
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Typography>

        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.8,
          }}
        >
          <IconButton
            size="medium"
            data-voice-close="1"
            onClick={handleCancel}
            sx={{ color: '#d32f2f', width: 18, height: 18 }}
          >
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.6,
              bgcolor: 'var(--spinner-input-bg)',
              borderRadius: '20px',
              padding: '1px',
            }}
          >
            <IconButton
              size="small"
              onPointerDown={(e) => {
                e.preventDefault();
                startHold('dec');
              }}
              onPointerUp={stopHold}
              onPointerLeave={stopHold}
              onPointerCancel={stopHold}
              disabled={tempHeight <= min}
              sx={{
                bgcolor: 'transparent',
                color: themeColor,
                width: 24,
                height: 24,
                borderRadius: '12px',
                touchAction: 'none',
              }}
            >
              <Remove sx={{ fontSize: 14 }} />
            </IconButton>

            <TextField
              type="text"
              value={tempHeight}
              onChange={handleHeightInputChange}
              variant="standard"
              size="small"
              InputProps={{
                disableUnderline: true,
                sx: {
                  width: '44px',
                  '& input': {
                    textAlign: 'center',
                    padding: '2px 0',
                    fontSize: '11px',
                    color: themeColor,
                    bgcolor: 'transparent',
                    fontFamily: 'monospace',
                    fontWeight: 500,
                  },
                },
              }}
            />

            <IconButton
              size="small"
              onPointerDown={(e) => {
                e.preventDefault();
                startHold('inc');
              }}
              onPointerUp={stopHold}
              onPointerLeave={stopHold}
              onPointerCancel={stopHold}
              disabled={tempHeight >= max}
              sx={{
                bgcolor: 'transparent',
                color: themeColor,
                width: 24,
                height: 24,
                borderRadius: '12px',
                touchAction: 'none',
              }}
            >
              <Add sx={{ fontSize: 14 }} />
            </IconButton>

            <Select
              variant="standard"
              value={tempHardness}
              onChange={(e) => setTempHardness(e.target.value)}
              onOpen={() => setIsHardnessMenuOpen(true)}
              onClose={() => setIsHardnessMenuOpen(false)}
              disableUnderline
              sx={{
                ml: 0.4,
                minWidth: 78,
                fontSize: '11px',
                fontFamily: 'monospace',
                fontWeight: 500,
                color: themeColor,
                '& .MuiSelect-select': {
                  padding: '2px 6px',
                  textAlign: 'center',
                },
                '& .MuiSelect-icon': {
                  color: 'var(--spinner-input-icon-color)'
                }
              }}
            >
              {hardnessOptions.map((opt) => (
                <MenuItem
                  key={String(opt)}
                  value={opt}
                  sx={{ fontSize: '11px', fontFamily: 'monospace' }}
                >
                  {String(opt)}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <IconButton
            size="small"
            onClick={handleApply}
            sx={{ color: '#2e7d32', width: 18, height: 18 }}
          >
            <Check sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

    </Box>
  );
};

export default AdditionSpinner;